import {
  useState,
  useMemo,
  useEffect,
  useRef,
  type ComponentProps,
  type ComponentType,
} from 'react'
import { ArrowRightCircle, Search } from 'lucide-react'
import { useProperties, usePropertyReservations } from '@/hooks/useApi'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { formatDateRange } from '@/lib/format-utils'

/**
 * Shape of a single property/reservation row this dropdown reads - only the
 * fields it actually consumes from GET /admin/properties and
 * GET /admin/properties/:id/reservations (both still untyped JS responses).
 */
interface PropertyOption {
  id: string
  name?: string
}

interface ReservationOption {
  id: string
  guest_name?: string | null
  check_in?: string | null
  check_out?: string | null
}

/**
 * Radix's public `DropdownMenuContentProps` deliberately omits
 * `onOpenAutoFocus` (it lives on an internal-only "Impl" props type that the
 * public wrapper's type strips out - see MenuContentImplPrivateProps in
 * @radix-ui/react-menu), but the underlying primitive still forwards and
 * honors it at runtime. This local alias just restores it to the type so we
 * can keep steering focus to the search input on open (see
 * handleOpenAutoFocus below) without resorting to `any`.
 */
const DropdownMenuContentWithAutoFocus = DropdownMenuContent as ComponentType<
  ComponentProps<typeof DropdownMenuContent> & { onOpenAutoFocus?: (event: Event) => void }
>

interface ReassignMessageDropdownProps {
  messageId: string
  currentPropertyId?: string | null
  currentReservationId?: string | null
  onReassign: (messageId: string, propertyId: string, reservationId: string | null) => void
  isReassigning?: boolean
}

/**
 * ReassignMessageDropdown - Dropdown for re-assigning messages to properties/reservations
 */
export function ReassignMessageDropdown({
  messageId,
  currentPropertyId,
  currentReservationId,
  onReassign,
  isReassigning,
}: ReassignMessageDropdownProps) {
  const {
    data: propertiesData,
    isLoading: isLoadingProperties,
    error: propertiesError,
  } = useProperties()
  const [openPropertyId, setOpenPropertyId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const itemRefs = useRef<Record<string, HTMLElement | null>>({})
  const { data: reservationsData, isLoading: isLoadingReservations } =
    usePropertyReservations(openPropertyId)

  // `id` is contractually optional on PropertyListItem (the schema is shared
  // with looser writer-side shapes), but the /admin/properties handler always
  // populates it (falling back to `property_id`) - filtering out any item
  // that somehow still lacks one is a defensive no-op in the normal case, and
  // avoids silently mis-keying/crashing on a genuinely malformed record
  // rather than guessing at a fallback id.
  const allProperties = useMemo<PropertyOption[]>(
    () =>
      (propertiesData?.properties || []).filter(
        (property): property is typeof property & { id: string } => Boolean(property.id)
      ),
    [propertiesData?.properties]
  )

  // Get reservations for the currently open property only
  // Same reasoning as allProperties above - `id` is contractually optional on
  // ReservationSummary, but the reservations handler always populates it
  // (falling back to `reservation_id`).
  const allReservations = useMemo<ReservationOption[]>(
    () =>
      (reservationsData?.reservations || []).filter(
        (reservation): reservation is typeof reservation & { id: string } => Boolean(reservation.id)
      ),
    [reservationsData?.reservations]
  )

  // Auto-focus search input when dropdown opens
  const handleOpenAutoFocus = (event: Event) => {
    // Prevent default focus behavior
    event.preventDefault()
    // Use requestAnimationFrame to ensure the DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus()
      })
    })
  }

  // Also try to focus when isOpen changes (backup method)
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 150)
      return () => clearTimeout(timer)
    } else {
      // Reset state when closed. This state lives on the always-mounted
      // trigger component, not the (Radix, unmounts-on-close) menu content,
      // so there's no remount to rely on for a fresh default here.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHighlightedIndex(-1)
      setOpenPropertyId(null)
      setSearchQuery('')
    }
  }, [isOpen])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => {
          const next = prev < navigableItems.length - 1 ? prev + 1 : 0
          // Scroll into view
          const itemKey =
            navigableItems[next]?.property?.id +
            '-' +
            (navigableItems[next]?.reservation?.id || 'none')
          itemRefs.current[itemKey]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
          return next
        })
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => {
          const next = prev > 0 ? prev - 1 : navigableItems.length - 1
          // Scroll into view
          const itemKey =
            navigableItems[next]?.property?.id +
            '-' +
            (navigableItems[next]?.reservation?.id || 'none')
          itemRefs.current[itemKey]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
          return next
        })
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < navigableItems.length) {
          const item = navigableItems[highlightedIndex]
          if (item.type === 'property') {
            // Open property submenu
            if (openPropertyId === item.property.id) {
              setOpenPropertyId(null)
            } else {
              setOpenPropertyId(item.property.id)
              setHighlightedIndex(0) // Reset to first item in submenu
            }
          } else if (item.type === 'reservation') {
            // Select reservation
            handleReservationSelect(item.property.id, item.reservation?.id || null)
          }
        }
        break
      case 'Escape':
        if (openPropertyId) {
          e.preventDefault()
          setOpenPropertyId(null)
          setHighlightedIndex(0)
        }
        break
    }
  }

  // Filter properties based on search query (properties only, not reservations)
  const filteredProperties = useMemo(() => {
    if (!searchQuery.trim()) return allProperties
    const query = searchQuery.toLowerCase()
    return allProperties.filter(
      property =>
        (property.name || '').toLowerCase().includes(query) ||
        property.id.toLowerCase().includes(query)
    )
  }, [allProperties, searchQuery])

  // Filter reservations for the currently open property based on search query
  const filteredReservations = useMemo(() => {
    if (!openPropertyId) return []
    if (!searchQuery.trim()) return allReservations
    const query = searchQuery.toLowerCase()
    return allReservations.filter(
      reservation =>
        (reservation.guest_name || '').toLowerCase().includes(query) ||
        reservation.id.toLowerCase().includes(query) ||
        (reservation.check_in && reservation.check_in.toLowerCase().includes(query)) ||
        (reservation.check_out && reservation.check_out.toLowerCase().includes(query))
    )
  }, [allReservations, searchQuery, openPropertyId])

  // Get all navigable items (properties + their reservations when submenu is open)
  // React Compiler can't preserve this memoization (builds the array via
  // .push in a loop) and skips optimizing it -- the manual useMemo below
  // still behaves correctly on its own, this only affects Compiler
  // optimization eligibility, not runtime correctness.
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const navigableItems = useMemo(() => {
    const items: Array<{
      type: 'property' | 'reservation'
      property: (typeof allProperties)[number]
      reservation: (typeof allReservations)[number] | null
      isNoReservation?: boolean
    }> = []
    filteredProperties.forEach(property => {
      items.push({ type: 'property', property, reservation: null })
      if (openPropertyId === property.id) {
        // Add "No reservation" option
        items.push({ type: 'reservation', property, reservation: null, isNoReservation: true })
        // Add reservations for this property
        filteredReservations.forEach(reservation => {
          items.push({ type: 'reservation', property, reservation })
        })
      }
    })
    return items
  }, [filteredProperties, openPropertyId, filteredReservations])

  const handleReservationSelect = (propertyId: string, reservationId: string | null) => {
    onReassign(messageId, propertyId, reservationId)
    setOpenPropertyId(null)
    setSearchQuery('')
  }

  const handlePropertySubOpen = (propertyId: string, open: boolean) => {
    if (open) {
      setOpenPropertyId(propertyId)
    } else {
      setOpenPropertyId(null)
    }
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              disabled={isReassigning}
              className="text-textMuted hover:text-text transition-colors disabled:opacity-50 opacity-60 hover:opacity-100 cursor-pointer"
            >
              <ArrowRightCircle className={`h-4 w-4 ${isReassigning ? 'animate-pulse' : ''}`} />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>Re-assign message</p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContentWithAutoFocus
        align="end"
        side="bottom"
        className="w-64"
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        <DropdownMenuLabel>Select Property</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Unified search input for both properties and reservations */}
        <div className="px-2 py-1.5">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-textMuted" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="Search properties..."
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value)
                setHighlightedIndex(-1) // Reset highlight when searching
              }}
              className="h-8 pl-8 text-sm"
              onClick={e => e.stopPropagation()}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>
        <DropdownMenuSeparator />
        {/* Properties list with smaller fixed max height */}
        <div className="max-h-[200px] overflow-y-auto">
          {isLoadingProperties ? (
            <DropdownMenuItem disabled>Loading properties...</DropdownMenuItem>
          ) : propertiesError ? (
            <DropdownMenuItem disabled>Error loading properties</DropdownMenuItem>
          ) : filteredProperties.length === 0 ? (
            <DropdownMenuItem disabled>
              {searchQuery ? 'No properties found' : 'No properties available'}
            </DropdownMenuItem>
          ) : (
            filteredProperties.map(property => {
              const itemKey = property.id + '-none'
              const isHighlighted =
                highlightedIndex >= 0 &&
                navigableItems[highlightedIndex]?.type === 'property' &&
                navigableItems[highlightedIndex]?.property?.id === property.id
              return (
                <DropdownMenuSub
                  key={property.id}
                  onOpenChange={open => handlePropertySubOpen(property.id, open)}
                >
                  <DropdownMenuSubTrigger
                    ref={el => {
                      itemRefs.current[itemKey] = el
                    }}
                    className={
                      property.id === currentPropertyId
                        ? 'bg-mintHover text-mint font-medium'
                        : isHighlighted
                          ? 'bg-accent'
                          : ''
                    }
                  >
                    {property.name || property.id}
                    {property.id === currentPropertyId && (
                      <span className="ml-auto text-xs text-mint font-semibold">(current)</span>
                    )}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-64">
                    <DropdownMenuLabel>Select Reservation</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      ref={el => {
                        itemRefs.current[property.id + '-no-reservation'] = el
                      }}
                      onClick={() => handleReservationSelect(property.id, null)}
                      className={
                        highlightedIndex >= 0 &&
                        navigableItems[highlightedIndex]?.type === 'reservation' &&
                        navigableItems[highlightedIndex]?.isNoReservation &&
                        navigableItems[highlightedIndex]?.property?.id === property.id
                          ? 'bg-accent'
                          : ''
                      }
                    >
                      No reservation
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {/* Reservations list with smaller fixed max height */}
                    <div className="max-h-[180px] overflow-y-auto">
                      {isLoadingReservations && openPropertyId === property.id ? (
                        <DropdownMenuItem disabled>Loading reservations...</DropdownMenuItem>
                      ) : openPropertyId === property.id ? (
                        filteredReservations.length === 0 ? (
                          <DropdownMenuItem disabled>
                            {searchQuery ? 'No reservations found' : 'No reservations'}
                          </DropdownMenuItem>
                        ) : (
                          filteredReservations.map(reservation => {
                            const itemKey = property.id + '-' + reservation.id
                            const isHighlighted =
                              highlightedIndex >= 0 &&
                              navigableItems[highlightedIndex]?.type === 'reservation' &&
                              navigableItems[highlightedIndex]?.reservation?.id ===
                                reservation.id &&
                              navigableItems[highlightedIndex]?.property?.id === property.id
                            return (
                              <DropdownMenuItem
                                key={reservation.id}
                                ref={el => {
                                  itemRefs.current[itemKey] = el
                                }}
                                onClick={() => handleReservationSelect(property.id, reservation.id)}
                                className={
                                  reservation.id === currentReservationId
                                    ? 'bg-mintHover text-mint'
                                    : isHighlighted
                                      ? 'bg-accent'
                                      : ''
                                }
                              >
                                <div className="flex flex-col gap-0.5 w-full">
                                  <span className="font-medium">
                                    {reservation.guest_name || 'Guest'}
                                  </span>
                                  {reservation.check_in && reservation.check_out ? (
                                    <span className="text-xs text-textMuted">
                                      {formatDateRange(reservation.check_in, reservation.check_out)}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-textMuted">{reservation.id}</span>
                                  )}
                                  {reservation.id === currentReservationId && (
                                    <span className="text-xs text-mint mt-0.5 font-semibold">
                                      (current)
                                    </span>
                                  )}
                                </div>
                              </DropdownMenuItem>
                            )
                          })
                        )
                      ) : null}
                    </div>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )
            })
          )}
        </div>
      </DropdownMenuContentWithAutoFocus>
    </DropdownMenu>
  )
}
